/*
 * New Leaf's own Yelp reviews — one source of truth.
 *
 * These are real New Leaf clients on New Leaf's own site, which is the single
 * allowed case for real names in site content. They must never render for
 * another tenant: showing them elsewhere is that company claiming someone
 * else's customers. Every consumer is gated on `branding.isNewLeaf`.
 *
 * `short` is the pull-quote for the scrolling banner — it has to read in about
 * two seconds as it passes. `quote` is the full review for the reviews page.
 */
export type Review = {
  name: string;
  location: string;
  date: string;
  /** Tight excerpt for the marquee. */
  short: string;
  /** Full review text for the reviews page. */
  quote: string;
  /** ISO date, for Review JSON-LD. Derived from `date`. */
  iso: string;
};

export const REVIEWS: Review[] = [
  {
    name: 'Barbara L.',
    location: 'San Diego, CA',
    date: 'Dec 2025',
    iso: '2025-12-01',
    short: 'It’s rare to find a business that treats your home with as much care as you do.',
    quote:
      'I am so grateful I found New Leaf Cleaning during such a high-stakes time. I’m currently listing my home for sale, and I really needed it to feel truly ‘move-in ready’ for potential buyers. Vikelas has been an absolute dream to work with, paying special attention to all those high-visibility areas that really need to sparkle. Beyond the incredible work, the communication is excellent and so easy. It’s rare to find a business that treats your home with as much care as you do — I can’t recommend them enough!',
  },
  {
    name: 'Jordan R.',
    location: 'St. Petersburg, FL',
    date: 'Apr 2026',
    iso: '2026-04-01',
    short: 'You can tell this isn’t just a business to him; it’s a reflection of his character.',
    quote:
      'Vikelas is the kind of person who brings genuine care and intention to everything he does, and New Leaf Cleaning is no exception. You can tell this isn’t just a business to him; it’s a reflection of his character. The attention to detail, the professionalism, and the pride he takes in his work speak for themselves. Highly recommend giving New Leaf Cleaning a shot!',
  },
  {
    name: 'Michael P.',
    location: 'Tampa Bay, FL',
    date: 'May 2026',
    iso: '2026-05-01',
    short: 'Three projects so far. Professional, courteous, consistently excellent results.',
    quote:
      'I have used New Leaf Cleaning on three of my projects so far and plan to continue working with them. They are professional, courteous, and consistently deliver excellent results. Their attention to detail and level of cleanliness has impressed both my clients and me every time. Finding a cleaning company that delivers this level of quality and value is not easy — I highly recommend them.',
  },
  {
    name: 'Bayleigh C.',
    location: 'Tallahassee, FL',
    date: 'Dec 2025',
    iso: '2025-12-01',
    short: 'I was overwhelmed before the cleaning. He made the whole thing easy and stress-free.',
    quote:
      'I can’t say enough good things about this service. The owner was incredibly kind, professional, and went above and beyond — even staying extra long to make sure my house was spotless and truly in tip-top shape. I was honestly overwhelmed before the cleaning, and he made the entire process so easy and stress-free. The results were amazing, and the care and attention to detail really showed.',
  },
  {
    name: 'Lori T.',
    location: 'Tampa, FL',
    date: 'Apr 2026',
    iso: '2026-04-01',
    short: 'Very thorough, and went above and beyond with my deep clean.',
    quote:
      'They were very thorough and went above and beyond with my deep clean. The owner and the cleaner were so nice and helpful. Would definitely use them again.',
  },
  {
    name: 'Michael T.',
    location: 'Austin, TX',
    date: 'Apr 2026',
    iso: '2026-04-01',
    short: 'The whole process from quote to payment was a breeze.',
    quote:
      'They were great! The whole process from quote to payment was a breeze. I would definitely recommend. Very fast and professional.',
  },
  {
    name: 'Jim G.',
    location: 'Tampa Bay, FL',
    date: 'May 2026',
    iso: '2026-05-01',
    short: 'Can’t believe what I got for the price I paid. Total value for my buck.',
    quote:
      'OMG! Can’t believe what I got for the price I paid! Total value for my buck. TKY, New Leaf.',
  },
];

/**
 * The same reviews shaped for Review/AggregateRating JSON-LD.
 *
 * This used to be a SECOND hardcoded list on reviews.tsx with slightly
 * different wording from the visible quotes. Google suppresses a review rich
 * result when the markup and the on-page text disagree, so the duplicate was
 * an SEO liability as well as a maintenance one. Deriving it guarantees they
 * match.
 */
export const REVIEWS_SCHEMA = REVIEWS.map((r) => ({
  reviewerName: r.name,
  rating: 5,
  date: r.iso,
  source: 'yelp' as const,
  text: r.quote,
}));
